// The render engine (§7). One instance per WebGL2 context: the preview canvas on the main thread, or an
// OffscreenCanvas in the export worker. Same passes, same uniforms; only sizes differ.
//
//   full-res source texture ──downsample──▶ preview (linear RGBA16F, ≤3072 px)
//   preview ──downsample + luma + blur──▶ guide (1/8 res, full image: never from a tile, §7)
//   input region ──colour (fused per-pixel passes)──▶ region target (premultiplied)
//   region ──warp (geometry, premultiplied resampling)──▶ output target (straight alpha)
//   output ──output transform──▶ canvas (display P3) or float target (export readback)
import {
  createTexture,
  drawFullscreen,
  enableFloat,
  Program,
  readFloat,
  readHalf,
  Target,
  type GL,
} from './gl/gl';
import { toHalf } from './gl/half';
import { BLUR_R, DOWNSAMPLE, GUIDE_LUMA, OUTPUT, WARP } from './shaders/passes';
import { HALVE, LANCZOS, OUTPUT_SHARPEN, PREMUL } from './shaders/exportPasses';
import { DETAIL_BLUR, NR, SHARP_COMBINE, SHARP_LUMA, TO_LAB } from './shaders/detailPasses';
import { HEAL, MASK_OVERLAY, SKIN_BILAT, SKIN_BLUR, SKIN_COMBINE, STROKE } from './shaders/localPasses';
import { BG_APPLY, BG_MINMAX, BG_REFINE, BG_SHARP, BOX_DOWN, PULL, PUSH0 } from './shaders/backgroundPasses';
import { VIGNETTE } from './shaders/effectsPasses';
import { GRAIN_TILE, makeGrainTile } from './grain';
import { maskThresholds, nrRangeSigma } from './cpuPipeline';
import type { TilePlan } from './exportPlan';
import { colorShader, featureKey, type ColorFeatures } from './shaders/colorShader';
import { apply3, outputToSource, toGl3, type GeometryInput, type M3, type Rect } from './geometry';
import { toGl, workingTo, toWorking, type ColorSpaceId } from '../color/matrix';
import { decoder } from '../color/transfer';
import { wbMatrix } from '../color/wb';
import { bakeLut, isIdentity } from '../color/curve';
import { HUE_BANDS } from '../color/hsl';
import type { HealSpot, LinearGeom, LocalMask, RadialGeom, Settings, Stroke } from '../model/types';
import type { CubeLut } from '../features/lut/cube';
import { defaultSettings as defaultSettingsLite } from '../model/defaults';
import type { DecodedImage } from '../io/decode/types';

export const PREVIEW_MAX = 3072;
export const APRON = 96;
/** Long edge of the stroke rasters (brush masks, skin, clean whites), covering the whole source. */
export const MASK_MAX = 2048;

export type SourceInfoGpu = { width: number; height: number; encoded: boolean };

export type OutputOptions = {
  space: ColorSpaceId;
  /** Composite onto `fill` (encoded, output space) — JPEG or forceColor. */
  flatten: boolean;
  fill: [number, number, number];
  clipping: boolean;
  /** Canvas only: how transparency is shown (§11.12 view modes): 0 none, 1 checkerboard, 2 on white,
   *  3 on black, 4 mask overlay (red), 5 matte (gray). */
  view?: number;
  /** §11.19 grain at output: amount 0..1, cell size in full-output px, and the map from drawn pixels to
   *  full-output pixels (px · xy + zw); the export pass sets the map per tile. */
  grain?: { amount: number; cell: number; map?: [number, number, number, number] };
};

export const PREVIEW_OUTPUT: OutputOptions = {
  space: 'p3',
  flatten: false,
  fill: [1, 1, 1],
  clipping: false,
};

/** Skin radius (§11.10): 0.35% of the source long edge, in source pixels. */
export const skinRadius = (W: number, H: number) => 0.0035 * Math.max(W, H);
/**
 * Blotch radius (CODE_REVIEW D29): the low-frequency layer is smoothed with an edge-preserving blur of
 * σ = 40 px (at most 8 % of the long edge on small images, at least 2r). A blotch only flattens under a
 * blur about its own size, and the target is 10–40 px blotches (the plan's G(low, 2r), 1.8–3.6 px on a
 * 512 px image, left them almost untouched). The bilateral range weight keeps feature edges.
 */
export const skinBlotchRadius = (W: number, H: number) =>
  Math.max(2 * skinRadius(W, H), Math.min(40, 0.08 * Math.max(W, H)));
export const hasSkin = (s: Settings) => s.skin.amount > 0 && s.skin.strokes.some((k) => !k.erase);
/** Any pass that needs neighbouring pixels (detail, skin): the fused one-pass preview cannot be used. */
export const hasBackground = (s: Settings) => s.background.mode !== 'off';
export const hasVignette = (s: Settings) => s.effects.vignette.amount !== 0;
export const hasGrain = (s: Settings) => s.effects.grain.amount > 0;
export const needsPasses = (s: Settings) =>
  hasDetail(s) || hasSkin(s) || hasBackground(s) || hasVignette(s) || hasGrain(s);

/** The colour features of the thumbnail superset variant (masks and LUTs stay per photo). */
const SUPERSET = {
  wb: true,
  exposure: true,
  tone: true,
  curve: true,
  whitesBlacks: true,
  color: true,
  hsl: true,
  cleanWhites: true,
  splitTone: true,
};

/** True when any detail control (§6.6) is active — the neighbourhood passes run only then. */
export const hasDetail = (s: Settings) =>
  s.detail.sharpAmount > 0 || s.detail.nrLuma > 0 || s.detail.nrColor > 0;

/** Which colour features a settings object turns on (zero settings → pass skipped, §6.2). */
export function colorFeatures(s: Settings): ColorFeatures {
  const l = s.light;
  return {
    wb: s.wb.temp !== 0 || s.wb.tint !== 0,
    exposure: l.exposure !== 0,
    tone: l.highlights !== 0 || l.shadows !== 0 || l.contrast !== 0,
    whitesBlacks: l.whites !== 0 || l.blacks !== 0,
    curve: !(
      isIdentity(s.curve.master) &&
      isIdentity(s.curve.r) &&
      isIdentity(s.curve.g) &&
      isIdentity(s.curve.b)
    ),
    ...colorFlags(s),
    masks: s.masks.length > 0,
    cleanWhites: s.cleanWhites.amount > 0,
    splitTone: s.effects.splitTone.shadowSat > 0 || s.effects.splitTone.highlightSat > 0,
  };
}

/** Converts a tagged 16-bit decode to linear-P3 half floats (RGBA16F upload, §6.1 decode step 4). */
export function taggedToLinearHalf(data: Uint16Array, space: ColorSpaceId): Uint16Array {
  const m = toWorking(space);
  const dec = decoder(space);
  const lut = new Float32Array(65536);
  for (let i = 0; i < 65536; i++) lut[i] = dec(i / 65535);
  const out = new Uint16Array(data.length);
  const [r0, r1, r2] = m[0];
  const [g0, g1, g2] = m[1];
  const [b0, b1, b2] = m[2];
  for (let i = 0; i < data.length; i += 4) {
    const r = lut[data[i]!]!,
      g = lut[data[i + 1]!]!,
      b = lut[data[i + 2]!]!;
    out[i] = toHalf(r0 * r + r1 * g + r2 * b);
    out[i + 1] = toHalf(g0 * r + g1 * g + g2 * b);
    out[i + 2] = toHalf(b0 * r + b1 * g + b2 * b);
    out[i + 3] = toHalf(data[i + 3]! / 65535);
  }
  return out;
}

export class Renderer {
  private progs = new Map<string, Program>();
  private full: WebGLTexture | null = null;
  private fullInfo: SourceInfoGpu | null = null;
  private preview: Target | null = null;
  private curveTex: WebGLTexture | null = null;
  private curveKey = '';
  private pool = new Map<string, Target>();
  private guideKey = '';
  private guideBase: Target | null = null;
  private halfOk: boolean | null = null;
  private sourceSerial = 0;
  private rasters = new Map<string, Target>();
  private blank: Target | null = null;
  private healKey = '';
  private healA: Target | null = null;
  /** Full-resolution healed source for tiles whose heal sources lie far away (CODE_REVIEW S10). */
  private healFull: Target | null = null;
  private healFullKey = '';
  private matteTex: WebGLTexture | null = null;
  private matteKey = '';
  private matteSerial = 0;
  private matteSize = { w: 1, h: 1 };
  private bgKey = '';
  private bgA: Target | null = null;
  private bgB: Target | null = null;
  private bgS1: Target | null = null;
  private bgS2: Target | null = null;
  private bgRefined: Target | null = null;
  private bgC: Target | null = null;
  private bgD: Target | null = null;
  /** The matte with shift/feather but WITHOUT refine strokes (the full-resolution tile path draws them). */
  private bgBase: Target | null = null;
  private bgShadow: Target | null = null;
  private lutTex: WebGLTexture | null = null;
  private grainTex: WebGLTexture | null = null;
  private grainSeedVal = -1;
  private lut: { id: string; kind: '1d' | '3d'; size: number; min: number[]; max: number[] } | null = null;
  /** Counters for tests (e.g. checking no second upload happens). */
  stats = { uploads: 0, renders: 0 };

  constructor(readonly gl: GL) {
    enableFloat(gl);
  }

  private prog(key: string, src: () => string): Program {
    let p = this.progs.get(key);
    if (!p) {
      p = new Program(this.gl, src());
      this.progs.set(key, p);
    }
    return p.use();
  }

  /**
   * One reusable float target per (format, slot). A request with a different size replaces the old
   * target, so memory stays bounded while the viewer resizes (§7 memory budget).
   */
  private target(w: number, h: number, fmt: 'rgba16f' | 'r16f' = 'rgba16f', slot = 0): Target {
    const key = `${fmt}:${slot}`;
    const cur = this.pool.get(key);
    if (cur && cur.width === w && cur.height === h) return cur;
    cur?.dispose();
    const t = new Target(this.gl, w, h, fmt);
    this.pool.set(key, t);
    return t;
  }
  releaseTargets() {
    for (const t of this.pool.values()) t.dispose();
    this.pool.clear();
  }

  get source(): SourceInfoGpu | null {
    return this.fullInfo;
  }
  get previewSize(): { width: number; height: number } | null {
    return this.preview ? { width: this.preview.width, height: this.preview.height } : null;
  }

  /** Uploads a decoded image and builds the preview texture. */
  setSource(img: DecodedImage, previewMax = PREVIEW_MAX) {
    this.disposeSource();
    const gl = this.gl;
    const maxTex = gl.getParameter(gl.MAX_TEXTURE_SIZE) as number;
    if (img.width > maxTex || img.height > maxTex)
      throw new Error(`image ${img.width}×${img.height} exceeds MAX_TEXTURE_SIZE ${maxTex}`);
    if (img.kind === 'u8') {
      this.full = createTexture(gl, img.width, img.height, 'rgba8', img.data);
      this.fullInfo = { width: img.width, height: img.height, encoded: true };
    } else {
      this.full = createTexture(
        gl,
        img.width,
        img.height,
        'rgba16f',
        taggedToLinearHalf(img.data, img.space),
      );
      this.fullInfo = { width: img.width, height: img.height, encoded: false };
    }
    this.stats.uploads++;
    this.sourceSerial++;
    const s = Math.min(1, previewMax / Math.max(img.width, img.height));
    const pw = Math.max(1, Math.round(img.width * s));
    const ph = Math.max(1, Math.round(img.height * s));
    // The preview texture is linear and PREMULTIPLIED, so hardware bilinear sampling is correct (§6.2).
    this.preview = new Target(gl, pw, ph, 'rgba16f');
    this.downsample(this.full, this.fullInfo, { x: 0, y: 0, w: 1, h: 1 }, this.preview, false, true);
  }

  disposeSource() {
    for (const t of this.rasters.values()) t.dispose();
    this.rasters.clear();
    this.healA?.dispose();
    this.healA = null;
    this.healKey = '';
    this.healFull?.dispose();
    this.healFull = null;
    this.healFullKey = '';
    // Nothing of the previous photo's render targets survives (CODE_REVIEW S11).
    this.releaseTargets();
    this.setMatte(null);
    for (const t of [this.bgA, this.bgB, this.bgC, this.bgD, this.bgS1, this.bgS2]) t?.dispose();
    this.bgA = this.bgB = this.bgC = this.bgD = this.bgS1 = this.bgS2 = this.bgRefined = this.bgShadow = null;
    this.bgBase = null;
    this.guideBase?.dispose();
    this.guideBase = null;
    this.guideKey = '';
    if (this.full) this.gl.deleteTexture(this.full);
    this.preview?.dispose();
    this.full = null;
    this.preview = null;
    this.fullInfo = null;
  }

  dispose() {
    this.disposeSource();
    this.releaseTargets();
    for (const p of this.progs.values()) p.dispose();
    this.progs.clear();
    if (this.curveTex) this.gl.deleteTexture(this.curveTex);
    this.curveTex = null;
    this.blank?.dispose();
    this.blank = null;
    this.setLut(null);
  }

  private downsample(
    src: WebGLTexture,
    info: SourceInfoGpu,
    rect: Rect,
    out: Target,
    inPremul = false,
    outPremul = false,
  ) {
    const p = this.prog('downsample', () => DOWNSAMPLE);
    out.bind();
    p.tex('uSrc', 0, src)
      .f('uSrcSize', info.width, info.height)
      .f('uRect', rect.x, rect.y, rect.w, rect.h)
      .f('uOutSize', out.width, out.height)
      .i('uEncoded', info.encoded ? 1 : 0)
      .i('uInPremul', inPremul ? 1 : 0)
      .i('uOutPremul', outPremul ? 1 : 0);
    drawFullscreen(this.gl);
  }

  /**
   * Low-res blurred luminance of the WHOLE image after WB + exposure (§6.4, §7). It depends only on the
   * source, WB and exposure, so it is cached on those; a contrast or curve drag does not rebuild it.
   */
  private guide(s: Settings): Target {
    const pv = this.preview!;
    const gw = Math.max(8, Math.round(pv.width / 8));
    const gh = Math.max(8, Math.round(pv.height / 8));
    const key = `${this.sourceSerial}|${s.wb.temp}|${s.wb.tint}|${s.light.exposure}|${gw}x${gh}`;
    const cached = this.pool.get('r16f:1');
    if (cached && key === this.guideKey) return cached;
    this.guideKey = key;
    // The 1/8 colour image does not depend on any setting: downsampled once per source.
    if (!this.guideBase || this.guideBase.width !== gw || this.guideBase.height !== gh) {
      this.guideBase?.dispose();
      this.guideBase = new Target(this.gl, gw, gh, 'rgba16f');
      this.downsample(
        pv.tex,
        { width: pv.width, height: pv.height, encoded: false },
        { x: 0, y: 0, w: 1, h: 1 },
        this.guideBase,
        true,
        false,
      );
    }
    const small = this.guideBase;
    const luma = this.target(gw, gh, 'r16f', 1);
    const wb = toGl(wbMatrix(s.wb.temp, s.wb.tint));
    this.prog('guideLuma', () => GUIDE_LUMA)
      .tex('uSrc', 0, small.tex)
      .f('uOutSize', gw, gh)
      .m3('uWb', wb)
      .f('uGain', 2 ** s.light.exposure);
    luma.bind();
    drawFullscreen(this.gl);
    const sigma = 0.015 * Math.max(gw, gh);
    const tmp = this.target(gw, gh, 'r16f', 2);
    const blur = this.prog('blurR', () => BLUR_R);
    tmp.bind();
    blur.tex('uSrc', 0, luma.tex).f('uOutSize', gw, gh).f('uDir', 1, 0).f('uSigma', sigma);
    drawFullscreen(this.gl);
    luma.bind();
    blur.tex('uSrc', 0, tmp.tex).f('uDir', 0, 1);
    drawFullscreen(this.gl);
    return luma;
  }

  private curveTexture(s: Settings): WebGLTexture {
    const key = JSON.stringify(s.curve);
    if (this.curveTex && key === this.curveKey) return this.curveTex;
    const data = new Uint16Array(1024 * 4);
    [s.curve.master, s.curve.r, s.curve.g, s.curve.b].forEach((pts, row) => {
      const lut = bakeLut(pts);
      for (let i = 0; i < 1024; i++) data[row * 1024 + i] = toHalf(lut[i]!);
    });
    if (this.curveTex) this.gl.deleteTexture(this.curveTex);
    // Built while the colour uniforms are being bound: creating a texture binds it on the active unit,
    // which would replace the guide (or the source) for this draw (CODE_REVIEW B1).
    this.curveTex = this.preserving(() => createTexture(this.gl, 1024, 4, 'r16f', data));
    this.curveKey = key;
    return this.curveTex;
  }

  /** Runs the colour stage on `region` of the input, into a premultiplied target of `rw × rh`. */
  /** Uniforms shared by every colour-shader mode. */
  private colorUniforms(p: Program, s: Settings, f: ColorFeatures, guide: Target) {
    p.tex('uGuide', 1, guide.tex)
      .m3('uWb', toGl(wbMatrix(s.wb.temp, s.wb.tint)))
      .f('uGain', 2 ** s.light.exposure)
      .f('uTone', s.light.highlights, s.light.shadows, s.light.contrast)
      .f('uWbk', s.light.whites, s.light.blacks);
    if (f.curve) p.tex('uCurve', 2, this.curveTexture(s));
    if (f.color) {
      p.f('uColorAdj', s.color.vibrance, s.color.saturation);
      if (f.hsl) {
        const v = new Float32Array(24);
        HUE_BANDS.forEach((b, i) => v.set([s.hsl[b].h, s.hsl[b].s, s.hsl[b].l], i * 3));
        this.gl.uniform3fv(p.loc('uHsl[0]'), v);
      }
    }
    if (f.masks) {
      this.maskUniforms(p, s.masks);
      const adj = new Float32Array(32),
        sat = new Float32Array(8),
        wb = new Float32Array(72);
      s.masks.slice(0, 8).forEach((m, i) => {
        adj.set([m.adj.exposure, m.adj.highlights, m.adj.shadows, m.adj.contrast], i * 4);
        sat[i] = m.adj.saturation;
        wb.set(toGl(wbMatrix(m.adj.temp, m.adj.tint)), i * 9);
      });
      this.gl.uniform4fv(p.loc('uMaskAdj[0]'), adj);
      this.gl.uniform1fv(p.loc('uMaskSat[0]'), sat);
      this.gl.uniformMatrix3fv(p.loc('uMaskWb[0]'), false, wb);
    }
    if (f.lut3 || f.lut1) {
      const l = this.lut!;
      const gl = this.gl;
      gl.activeTexture(gl.TEXTURE6);
      gl.bindTexture(f.lut3 ? gl.TEXTURE_3D : gl.TEXTURE_2D, this.lutTex);
      gl.uniform1i(p.loc(f.lut3 ? 'uLut3' : 'uLut1'), 6);
      p.f('uLutMin', l.min[0]!, l.min[1]!, l.min[2]!)
        .f('uLutMax', l.max[0]!, l.max[1]!, l.max[2]!)
        .f('uLutSize', l.size)
        .f('uLutAmt', Math.min(1, Math.max(0, s.lut.intensity / 100)));
    }
    if (f.splitTone) {
      const t = s.effects.splitTone;
      p.f('uSplit', t.shadowHue, t.shadowSat, t.highlightHue, t.highlightSat).f('uSplitBalance', t.balance);
    }
    if (f.cleanWhites) {
      const brush = s.cleanWhites.strokes;
      p.f('uCw', s.cleanWhites.amount, brush ? 1 : 0).tex(
        'uCwMask',
        5,
        brush ? this.raster([brush]).tex : this.blankTex(),
      );
    }
  }

  /** Mask geometry uniforms (MASK_FNS), shared by the colour shader and the overlay. */
  private maskUniforms(p: Program, masks: LocalMask[]) {
    const ms = masks.slice(0, 8);
    const kind = new Int32Array(8),
      inv = new Int32Array(8),
      A = new Float32Array(32),
      B = new Float32Array(32);
    const brushes: Stroke[][] = [];
    ms.forEach((m, i) => {
      inv[i] = m.invert ? 1 : 0;
      if (m.kind === 'linear') {
        const g = m.geom as LinearGeom;
        A.set([g.x0, g.y0, g.x1, g.y1], i * 4);
      } else if (m.kind === 'radial') {
        const g = m.geom as RadialGeom;
        kind[i] = 1;
        A.set([g.cx, g.cy, g.rx, g.ry], i * 4);
        B.set([(g.angle * Math.PI) / 180, g.feather, 0, 0], i * 4);
      } else {
        kind[i] = 2;
        B.set([0, 0, brushes.length, 0], i * 4);
        brushes.push((m.geom as { strokes: Stroke[] }).strokes);
      }
    });
    const gl = this.gl;
    p.i('uMaskN', ms.length).f('uSrcDim', this.fullInfo!.width, this.fullInfo!.height);
    gl.uniform1iv(p.loc('uMaskKind[0]'), kind);
    gl.uniform1iv(p.loc('uMaskInv[0]'), inv);
    gl.uniform4fv(p.loc('uMaskA[0]'), A);
    gl.uniform4fv(p.loc('uMaskB[0]'), B);
    p.tex('uBrush0', 3, brushes.length ? this.raster(brushes.slice(0, 4)).tex : this.blankTex());
    p.tex('uBrush1', 4, brushes.length > 4 ? this.raster(brushes.slice(4, 8)).tex : this.blankTex());
  }

  /** Runs `fn` and restores the framebuffer, viewport and scissor state it may change. */
  private preserving<T>(fn: () => T): T {
    const gl = this.gl;
    const fb = gl.getParameter(gl.FRAMEBUFFER_BINDING) as WebGLFramebuffer | null;
    const vp = gl.getParameter(gl.VIEWPORT) as Int32Array;
    const sc = gl.isEnabled(gl.SCISSOR_TEST);
    const box = gl.getParameter(gl.SCISSOR_BOX) as Int32Array;
    const prog = gl.getParameter(gl.CURRENT_PROGRAM) as WebGLProgram | null;
    const unit = gl.getParameter(gl.ACTIVE_TEXTURE) as number;
    const texs: (WebGLTexture | null)[] = [];
    for (let i = 0; i < 8; i++) {
      gl.activeTexture(gl.TEXTURE0 + i);
      texs.push(gl.getParameter(gl.TEXTURE_BINDING_2D) as WebGLTexture | null);
    }
    gl.activeTexture(unit);
    try {
      return fn();
    } finally {
      gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
      gl.viewport(vp[0]!, vp[1]!, vp[2]!, vp[3]!);
      if (sc) gl.enable(gl.SCISSOR_TEST);
      else gl.disable(gl.SCISSOR_TEST);
      gl.scissor(box[0]!, box[1]!, box[2]!, box[3]!);
      gl.useProgram(prog);
      texs.forEach((t, i) => {
        gl.activeTexture(gl.TEXTURE0 + i);
        gl.bindTexture(gl.TEXTURE_2D, t);
      });
      gl.activeTexture(unit);
    }
  }

  private blankTex(): WebGLTexture {
    if (!this.blank) this.preserving(() => this.makeBlank());
    return this.blank!.tex;
  }
  private makeBlank() {
    {
      this.blank = new Target(this.gl, 1, 1, 'rgba16f');
      this.blank.bind();
      this.gl.clearColor(0, 0, 0, 0);
      this.gl.clear(this.gl.COLOR_BUFFER_BIT);
    }
  }

  /**
   * Rasterizes up to four stroke sets (one per channel) into an RGBA16F mask covering the whole source,
   * long edge ≤ MASK_MAX (§11.4 brush masks; also the skin and clean-whites brushes). Cached by content.
   * Stroke size = diameter as a fraction of the source short edge; points are source-normalized.
   * DEVIATION: brush masks are rasterized once at ≤ 2048 px over the whole source and sampled bilinearly,
   * instead of at full size per export tile (masks are soft, so the difference is not visible).
   */
  raster(sets: Stroke[][]): Target {
    return this.preserving(() => this.rasterNow(sets));
  }
  private rasterNow(sets: Stroke[][]): Target {
    const gl = this.gl;
    const info = this.fullInfo!;
    const k = Math.min(1, MASK_MAX / Math.max(info.width, info.height));
    const w = Math.max(1, Math.round(info.width * k)),
      h = Math.max(1, Math.round(info.height * k));
    const key = `${this.sourceSerial}|${w}x${h}|${JSON.stringify(sets)}`;
    const hit = this.rasters.get(key);
    if (hit) {
      this.rasters.delete(key);
      this.rasters.set(key, hit);
      return hit;
    }
    const t = new Target(gl, w, h, 'rgba16f');
    t.bind();
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    this.drawStrokes(sets, (q) => [q.x * w, q.y * h], Math.min(w, h));
    this.rasters.set(key, t);
    while (this.rasters.size > 8) {
      const [k0, t0] = this.rasters.entries().next().value as [string, Target];
      t0.dispose();
      this.rasters.delete(k0);
    }
    return t;
  }

  /** Draws stroke sets (one per channel) into the bound target; `toPx` maps a source-normalized point
   *  to target pixels, `rScale` = target pixels per unit of stroke size (a short-edge fraction). */
  private drawStrokes(
    sets: Stroke[][],
    toPx: (q: { x: number; y: number }) => [number, number],
    rScale: number,
  ) {
    const gl = this.gl;
    const p = this.prog('stroke', () => STROKE);
    gl.enable(gl.BLEND);
    gl.enable(gl.SCISSOR_TEST);
    sets.slice(0, 4).forEach((strokes, ch) => {
      gl.colorMask(ch === 0, ch === 1, ch === 2, ch === 3);
      for (const st of strokes) {
        if (st.erase) {
          gl.blendEquation(gl.FUNC_ADD);
          gl.blendFunc(gl.ZERO, gl.ONE_MINUS_SRC_COLOR);
        } else {
          gl.blendEquation(gl.MAX);
          gl.blendFunc(gl.ONE, gl.ONE);
        }
        const r = (st.size / 2) * rScale;
        for (let i = 0; i < st.points.length; i += 63) {
          const chunk = st.points.slice(i, i + 64);
          const pts = new Float32Array(192);
          let x0 = Infinity,
            y0 = Infinity,
            x1 = -Infinity,
            y1 = -Infinity;
          chunk.forEach((q, j) => {
            const [px, py] = toPx(q);
            pts.set([px, py, q.p], j * 3);
            x0 = Math.min(x0, px);
            y0 = Math.min(y0, py);
            x1 = Math.max(x1, px);
            y1 = Math.max(y1, py);
          });
          gl.uniform3fv(p.loc('uPts[0]'), pts);
          p.i('uN', chunk.length).f('uR', r).f('uFeather', st.feather).f('uFlow', st.flow);
          const sx = Math.max(0, Math.floor(x0 - r - 2)),
            sy = Math.max(0, Math.floor(y0 - r - 2));
          gl.scissor(
            sx,
            sy,
            Math.max(0, Math.ceil(x1 + r + 2) - sx),
            Math.max(0, Math.ceil(y1 + r + 2) - sy),
          );
          drawFullscreen(gl);
          if (chunk.length < 64) break;
        }
      }
    });
    gl.colorMask(true, true, true, true);
    gl.blendEquation(gl.FUNC_ADD);
    gl.blendFunc(gl.ONE, gl.ZERO);
    gl.disable(gl.BLEND);
    gl.disable(gl.SCISSOR_TEST);
  }

  /**
   * Heal/clone spots (§11.8, §6.2 pass 1) applied in order to `a` (linear values covering part of the
   * source: texel → source px = map.xy + texel · map.zw). Each spot is drawn into a patch-sized scratch
   * target (as big as the largest spot box, not the whole of `a`: memory, CODE_REVIEW S10) and copied
   * back, so later spots see earlier ones. Returns `a`.
   */
  private applyHeal(a: Target, map: [number, number, number, number], spots: HealSpot[]): Target {
    const gl = this.gl;
    const info = this.fullInfo!;
    const W = info.width,
      H = info.height;
    const boxes = spots.map((sp) => this.healBox(sp, a, map));
    const bw = Math.max(1, ...boxes.map((b) => (b ? b[2] - b[0] : 0)));
    const bh = Math.max(1, ...boxes.map((b) => (b ? b[3] - b[1] : 0)));
    const scratch = this.target(bw, bh, 'rgba16f', 21);
    const p = this.prog('heal', () => HEAL);
    spots.forEach((sp, k) => {
      const box = boxes[k];
      if (!box) return;
      const [tx0, ty0, tx1, ty1] = box;
      const pts = sp.stroke.points;
      const step = Math.max(1, Math.ceil(pts.length / 31));
      const sel = pts.filter((_, i) => i % step === 0).slice(0, 31);
      if (sel[sel.length - 1] !== pts[pts.length - 1]) sel.push(pts[pts.length - 1]!);
      const r = (sp.stroke.size / 2) * Math.min(W, H);
      const u = new Float32Array(96);
      sel.forEach((q, j) => u.set([q.x * W, q.y * H, q.p], j * 3));
      scratch.bind();
      gl.viewport(0, 0, tx1 - tx0, ty1 - ty0);
      gl.uniform3fv(p.loc('uPts[0]'), u);
      p.tex('uSrc', 0, a.tex)
        .f('uTexSize', a.width, a.height)
        .f('uMap', ...map)
        .f('uOutOrigin', tx0, ty0)
        .i('uN', sel.length)
        .f('uR', r)
        .f('uFeather', sp.stroke.feather)
        .f('uOpacity', sp.opacity)
        .f('uOffset', sp.offset.x * W, sp.offset.y * H)
        .i('uClone', sp.kind === 'clone' ? 1 : 0);
      drawFullscreen(gl);
      gl.bindFramebuffer(gl.READ_FRAMEBUFFER, scratch.fbo);
      gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, a.fbo);
      gl.blitFramebuffer(0, 0, tx1 - tx0, ty1 - ty0, tx0, ty0, tx1, ty1, gl.COLOR_BUFFER_BIT, gl.NEAREST);
      gl.bindFramebuffer(gl.READ_FRAMEBUFFER, null);
      gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, null);
    });
    return a;
  }

  /** A spot's bounding box in `a`'s texels ([x0, y0, x1, y1]), or null when it misses `a`. */
  private healBox(sp: HealSpot, a: Target, map: [number, number, number, number]) {
    const info = this.fullInfo!;
    const W = info.width,
      H = info.height;
    const pts = sp.stroke.points;
    if (!pts.length) return null;
    const r = (sp.stroke.size / 2) * Math.min(W, H);
    let x0 = Infinity,
      y0 = Infinity,
      x1 = -Infinity,
      y1 = -Infinity;
    for (const q of pts) {
      x0 = Math.min(x0, q.x * W - r);
      y0 = Math.min(y0, q.y * H - r);
      x1 = Math.max(x1, q.x * W + r);
      y1 = Math.max(y1, q.y * H + r);
    }
    const tx0 = Math.max(0, Math.floor((x0 - map[0]) / map[2]) - 1),
      ty0 = Math.max(0, Math.floor((y0 - map[1]) / map[3]) - 1);
    const tx1 = Math.min(a.width, Math.ceil((x1 - map[0]) / map[2]) + 1),
      ty1 = Math.min(a.height, Math.ceil((y1 - map[1]) / map[3]) + 1);
    return tx1 <= tx0 || ty1 <= ty0 ? null : ([tx0, ty0, tx1, ty1] as const);
  }

  /** The whole source, linear, with every heal spot applied in order; cached on the spot list (S10). */
  private healedFull(s: Settings): Target {
    const key = `${this.sourceSerial}|${JSON.stringify(s.heal)}`;
    if (key === this.healFullKey && this.healFull) return this.healFull;
    return this.preserving(() => {
      const info = this.fullInfo!;
      if (!this.healFull || this.healFull.width !== info.width || this.healFull.height !== info.height) {
        this.healFull?.dispose();
        this.healFull = new Target(this.gl, info.width, info.height, 'rgba16f');
      }
      this.downsample(this.full!, info, { x: 0, y: 0, w: 1, h: 1 }, this.healFull, false, false);
      this.applyHeal(this.healFull, [0, 0, 1, 1], s.heal);
      this.healFullKey = key;
      return this.healFull;
    });
  }

  /** The preview texture with heal/clone spots applied (cached on the spots). */
  private previewTex(s: Settings): WebGLTexture {
    const pv = this.preview!;
    if (!s.heal.length) return pv.tex;
    const key = `${this.sourceSerial}|${JSON.stringify(s.heal)}`;
    if (key === this.healKey && this.healA) return this.healA.tex;
    return this.preserving(() => this.healPreview(s, key));
  }
  private healPreview(s: Settings, key: string): WebGLTexture {
    const pv = this.preview!;
    if (!this.healA || this.healA.width !== pv.width || this.healA.height !== pv.height) {
      this.healA?.dispose();
      this.healA = new Target(this.gl, pv.width, pv.height, 'rgba16f');
    }
    const gl = this.gl;
    gl.bindFramebuffer(gl.READ_FRAMEBUFFER, pv.fbo);
    gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, this.healA.fbo);
    gl.blitFramebuffer(0, 0, pv.width, pv.height, 0, 0, pv.width, pv.height, gl.COLOR_BUFFER_BIT, gl.NEAREST);
    gl.bindFramebuffer(gl.READ_FRAMEBUFFER, null);
    gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, null);
    const info = this.fullInfo!;
    this.applyHeal(this.healA, [0, 0, info.width / pv.width, info.height / pv.height], s.heal);
    this.healKey = key;
    return this.healA.tex;
  }

  /**
   * Skin (§11.10, pass 13) on a target: low = G(img, r), smooth = G(low, 2r), out = mix(img, smooth + img −
   * low, mask · amount). `toSrc` maps target pixels to source-normalized coordinates (for the mask);
   * `scale` = target pixels per source pixel.
   */
  private skinStage(src: Target, s: Settings, toSrc: M3, scale: number): Target {
    const gl = this.gl;
    const info = this.fullInfo!;
    const w = src.width,
      h = src.height;
    const r = Math.max(0.3, skinRadius(info.width, info.height) * scale);
    const blur = (input: Target, sigma: number, tmpSlot: number, outSlot: number) => {
      const R = Math.min(128, Math.ceil(sigma * 3));
      const tmp = this.target(w, h, 'rgba16f', tmpSlot);
      const out = this.target(w, h, 'rgba16f', outSlot);
      const p = this.prog('skinBlur', () => SKIN_BLUR);
      tmp.bind();
      p.tex('uSrc', 0, input.tex).f('uDir', 1, 0).f('uSigma', sigma).i('uR', R);
      drawFullscreen(gl);
      out.bind();
      p.tex('uSrc', 0, tmp.tex).f('uDir', 0, 1);
      drawFullscreen(gl);
      return out;
    };
    const low = blur(src, r, 22, 23);
    // Edge-preserving blur of the low layer (D29); taps are spaced so a pass stays ≤ ~64 samples.
    const rb = Math.max(0.6, skinBlotchRadius(info.width, info.height) * scale);
    const RB = Math.ceil(rb * 3);
    const stepB = Math.max(1, Math.round(RB / 32));
    const bil = this.prog('skinBilat', () => SKIN_BILAT);
    const tmpB = this.target(w, h, 'rgba16f', 22);
    const smooth = this.target(w, h, 'rgba16f', 24);
    tmpB.bind();
    bil.tex('uSrc', 0, low.tex).f('uDir', 1, 0).f('uSigma', rb).i('uR', RB).i('uStep', stepB);
    drawFullscreen(gl);
    smooth.bind();
    bil.tex('uSrc', 0, tmpB.tex).f('uDir', 0, 1);
    drawFullscreen(gl);
    const out = this.target(w, h, 'rgba16f', 25);
    out.bind();
    this.prog('skinCombine', () => SKIN_COMBINE)
      .tex('uImg', 0, src.tex)
      .tex('uLow', 1, low.tex)
      .tex('uSmooth', 2, smooth.tex)
      .tex('uMask', 3, this.raster([s.skin.strokes]).tex)
      .m3('uToSrc', toGl3(toSrc))
      .f('uAmount', s.skin.amount);
    drawFullscreen(gl);
    return out;
  }

  /** Compiles the thumbnail superset program ahead of time (Simple mode opens with it). */
  prewarmSuperset() {
    const f = { ...colorFeatures(defaultSettingsLite()), ...SUPERSET } as ColorFeatures;
    this.preserving(() => this.prog(`color:warpLinear:${featureKey(f)}`, () => colorShader(f, 'warpLinear')));
  }

  // ── Effects (§11.19) ─────────────────────────────────────────────────────

  /** Vignette after geometry on a straight-alpha target at `offset` inside a `full`-sized output frame. */
  private vignette(src: Target, s: Settings, offset: [number, number], full: [number, number]): Target {
    const v = s.effects.vignette;
    const out = this.target(src.width, src.height, 'rgba16f', src === this.pool.get('rgba16f:19') ? 29 : 19);
    out.bind();
    this.prog('vignette', () => VIGNETTE)
      .tex('uSrc', 0, src.tex)
      .f('uOffset', offset[0], offset[1])
      .f('uFull', full[0], full[1])
      .f('uVig', v.amount, v.midpoint, v.feather);
    drawFullscreen(this.gl);
    return out;
  }

  /** The photo's grain seed (§11.19): a new seed regenerates the 256² tile. */
  setGrainSeed(seed: number) {
    if (seed === this.grainSeedVal && this.grainTex) return;
    this.grainSeedVal = seed;
    const gl = this.gl;
    this.preserving(() => {
      if (this.grainTex) gl.deleteTexture(this.grainTex);
      const t = gl.createTexture()!;
      gl.bindTexture(gl.TEXTURE_2D, t);
      gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
      const half = Uint16Array.from(makeGrainTile(seed), toHalf);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.R16F, GRAIN_TILE, GRAIN_TILE, 0, gl.RED, gl.HALF_FLOAT, half);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      this.grainTex = t;
    });
  }

  private grainUniforms(p: Program, o: OutputOptions, map: [number, number, number, number]) {
    const g = o.grain;
    if (!g || g.amount <= 0 || !this.grainTex) {
      p.f('uGrainAmt', 0);
      return;
    }
    const m = g.map ?? map;
    p.tex('uGrain', 5, this.grainTex)
      .f('uGrainAmt', g.amount)
      .f('uGrainCell', g.cell)
      .f('uGrainMap', m[0], m[1], m[2], m[3]);
  }

  // ── LUT (§11.6, pass 14) ───────────────────────────────────────────────────

  /** Colour features for these settings; the LUT pass is on only when its texture is loaded here. */
  private feats(s: Settings): ColorFeatures {
    const on = !!this.lut && s.lut.id === this.lut.id && s.lut.intensity > 0;
    return { ...colorFeatures(s), lut3: on && this.lut!.kind === '3d', lut1: on && this.lut!.kind === '1d' };
  }

  /**
   * Uploads a `.cube` LUT: 3D → RGBA float 3D texture with trilinear sampling; 1D → a 1-row 2D texture
   * (resampled to ≤ 4096 entries). Float32 when linear filtering of float textures is available, else 16F.
   */
  setLut(l: CubeLut | null, id = '') {
    const gl = this.gl;
    if (l && this.lut?.id === id && this.lutTex) return;
    if (this.lutTex) gl.deleteTexture(this.lutTex);
    this.lutTex = null;
    this.lut = null;
    if (!l) return;
    this.preserving(() => {
      const f32 = !!gl.getExtension('OES_texture_float_linear');
      const internal = f32 ? gl.RGBA32F : gl.RGBA16F;
      const t = gl.createTexture()!;
      let size = l.size;
      if (l.kind === '3d') {
        const n = l.size ** 3;
        const rgba = new Float32Array(n * 4);
        for (let i = 0; i < n; i++)
          rgba.set([l.data[i * 3]!, l.data[i * 3 + 1]!, l.data[i * 3 + 2]!, 1], i * 4);
        gl.bindTexture(gl.TEXTURE_3D, t);
        gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
        gl.texImage3D(gl.TEXTURE_3D, 0, internal, l.size, l.size, l.size, 0, gl.RGBA, gl.FLOAT, rgba);
        for (const p of [gl.TEXTURE_WRAP_S, gl.TEXTURE_WRAP_T, gl.TEXTURE_WRAP_R])
          gl.texParameteri(gl.TEXTURE_3D, p, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      } else {
        size = Math.min(4096, l.size);
        const rgba = new Float32Array(size * 4);
        for (let i = 0; i < size; i++) {
          const x = (i / (size - 1)) * (l.size - 1);
          const i0 = Math.min(l.size - 2, Math.floor(x)),
            tt = x - i0;
          for (let c = 0; c < 3; c++)
            rgba[i * 4 + c] = l.data[i0 * 3 + c]! + (l.data[(i0 + 1) * 3 + c]! - l.data[i0 * 3 + c]!) * tt;
          rgba[i * 4 + 3] = 1;
        }
        gl.bindTexture(gl.TEXTURE_2D, t);
        gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
        gl.texImage2D(gl.TEXTURE_2D, 0, internal, size, 1, 0, gl.RGBA, gl.FLOAT, rgba);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      }
      this.lutTex = t;
      this.lut = { id: id || 'lut', kind: l.kind, size, min: [...l.domainMin], max: [...l.domainMax] };
    });
  }

  // ── Background removal (§11.12, pass 16) ──────────────────────────────────

  /** Uploads the base matte (model or key, ≤ 2048 px, 0..255) covering the whole source. */
  setMatte(m: { width: number; height: number; data: Uint8Array } | null, key = '') {
    if (m && key && key === this.matteKey && this.matteTex) return;
    if (this.matteTex) this.gl.deleteTexture(this.matteTex);
    this.matteTex = null;
    this.matteKey = '';
    this.bgKey = '';
    if (!m) return;
    this.matteTex = this.preserving(() => createTexture(this.gl, m.width, m.height, 'r8', m.data));
    this.matteKey = key || `m${++this.matteSerial}`;
    this.matteSize = { w: m.width, h: m.height };
  }
  get hasMatte() {
    return !!this.matteTex;
  }

  /** Refined matte (refine strokes, shift, feather) and the shadow source, cached on their settings. */
  private bgTextures(s: Settings): { matte: Target; shadow: Target | null; base: Target } {
    const b = s.background;
    const key = `${this.matteKey}|${JSON.stringify(b.refine)}|${JSON.stringify(b.edge)}|${JSON.stringify(b.shadow)}`;
    if (key === this.bgKey && this.bgRefined)
      return { matte: this.bgRefined, shadow: this.bgShadow, base: this.bgBase ?? this.bgRefined };
    return this.preserving(() => {
      const gl = this.gl;
      const { w, h } = this.matteSize;
      const long = Math.max(w, h);
      const mk = (old: Target | null, ww: number, hh: number) => {
        if (old && old.width === ww && old.height === hh) return old;
        old?.dispose();
        return new Target(gl, ww, hh, 'r16f');
      };
      const keep = b.refine.filter((k) => !k.erase).map((k) => ({ ...k, erase: false }));
      const remove = b.refine.filter((k) => k.erase).map((k) => ({ ...k, erase: false }));
      // Refine strokes, then edge shift and feather, ping-ponging between two targets; returns the result.
      const chain = (refineTex: WebGLTexture, cur: Target, other: Target): Target => {
        cur.bind();
        this.prog('bgRefine', () => BG_REFINE)
          .tex('uMatte', 0, this.matteTex!)
          .tex('uRefine', 1, refineTex)
          .f('uSize', w, h);
        drawFullscreen(gl);
        const twoPass = (draw: (p: Program) => void, prog: Program) => {
          other.bind();
          prog.tex('uSrc', 0, cur.tex).f('uDir', 1, 0);
          draw(prog);
          drawFullscreen(gl);
          cur.bind();
          prog.tex('uSrc', 0, other.tex).f('uDir', 0, 1);
          drawFullscreen(gl);
        };
        if (b.edge.shift !== 0) {
          const R = Math.min(64, Math.round((Math.abs(b.edge.shift) * long) / 1000));
          if (R > 0) {
            const p = this.prog('bgMinMax', () => BG_MINMAX);
            twoPass((q) => q.i('uR', R).i('uDilate', b.edge.shift > 0 ? 1 : 0), p);
          }
        }
        if (b.edge.feather > 0) {
          const sigma = Math.max(0.3, (b.edge.feather * long) / 1000 / 2);
          const p = this.prog('skinBlur', () => SKIN_BLUR);
          twoPass((q) => q.f('uSigma', sigma).i('uR', Math.min(128, Math.ceil(3 * sigma))), p);
        }
        return cur;
      };
      this.bgA = mk(this.bgA, w, h);
      this.bgB = mk(this.bgB, w, h);
      const hasRefine = keep.length > 0 || remove.length > 0;
      const cur = chain(hasRefine ? this.raster([keep, remove]).tex : this.blankTex(), this.bgA, this.bgB);
      this.bgRefined = cur;
      if (hasRefine) {
        this.bgC = mk(this.bgC, w, h);
        this.bgD = mk(this.bgD, w, h);
        this.bgBase = chain(this.blankTex(), this.bgC, this.bgD);
      } else this.bgBase = cur;
      this.bgShadow = null;
      if (b.shadow.enabled) {
        const k = Math.min(1, 512 / long);
        const sw = Math.max(1, Math.round(w * k)),
          sh = Math.max(1, Math.round(h * k));
        this.bgS1 = mk(this.bgS1, sw, sh);
        this.bgS2 = mk(this.bgS2, sw, sh);
        this.bgS1.bind();
        this.prog('boxDown', () => BOX_DOWN)
          .tex('uSrc', 0, cur.tex)
          .f('uOutSize', sw, sh);
        drawFullscreen(gl);
        const sigma = Math.max(0.5, (b.shadow.blur / 1000) * Math.max(sw, sh));
        const R = Math.min(128, Math.ceil(3 * sigma));
        const p = this.prog('skinBlur', () => SKIN_BLUR);
        this.bgS2.bind();
        p.tex('uSrc', 0, this.bgS1.tex).f('uDir', 1, 0).f('uSigma', sigma).i('uR', R);
        drawFullscreen(gl);
        this.bgS1.bind();
        p.tex('uSrc', 0, this.bgS2.tex).f('uDir', 0, 1);
        drawFullscreen(gl);
        this.bgShadow = this.bgS1;
      }
      this.bgKey = key;
      return { matte: this.bgRefined, shadow: this.bgShadow, base: this.bgBase };
    });
  }

  /**
   * Full-resolution matte for one tile region (CODE_REVIEW D18/D15): inside the soft band of the cached
   * ≤ 2048 px matte, alpha is re-estimated from the tile's own colours (BG_SHARP), and the refine strokes
   * are drawn again at full resolution. Returns null when the matte is already at (about) source size.
   */
  private tileMatte(
    src: Target,
    s: Settings,
    matte: Target,
    toSrc: M3,
    premul: boolean,
    region: Rect,
    outPerSrc = 1,
  ): Target | null {
    const info = this.fullInfo!;
    const factor = Math.max(info.width, info.height) / Math.max(this.matteSize.w, this.matteSize.h);
    if (factor <= 1.05) return null;
    // A downsized export (Instagram 1080, WhatsApp 1600 from 24 MP) has no more pixels than the cached
    // matte: the full-resolution edge would be thrown away by the resize, so skip it (owner, save < 5 s).
    if (outPerSrc * factor <= 1.05) return null;
    const gl = this.gl;
    const w = src.width,
      h = src.height;
    // The enlarged band is ~2 matte texels wide: the window must reach solid pixels on both sides.
    const R = Math.min(48, Math.ceil(2 * factor) + 3);
    const step = Math.max(1, Math.round(R / 8));
    const b = s.background;
    const keep = b.refine.filter((k) => !k.erase).map((k) => ({ ...k, erase: false }));
    const remove = b.refine.filter((k) => k.erase).map((k) => ({ ...k, erase: false }));
    let refine: WebGLTexture | null = null;
    if (keep.length || remove.length) {
      const rt = this.target(w, h, 'rgba16f', 44);
      rt.bind();
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      // Tile pixels per source pixel (the region path keeps the source's pixel grid, so this is ~1).
      const k = w / (region.w * info.width);
      this.drawStrokes(
        [keep, remove],
        (q) => [((q.x - region.x) * w) / region.w, ((q.y - region.y) * h) / region.h],
        Math.min(info.width, info.height) * k,
      );
      refine = rt.tex;
    }
    const out = this.target(w, h, 'r16f', 45);
    out.bind();
    this.prog('bgSharp', () => BG_SHARP)
      .tex('uImg', 0, src.tex)
      .tex('uMatte', 1, matte.tex)
      .tex('uRefine', 2, refine ?? this.blankTex())
      .m3('uToSrc', toGl3(toSrc))
      .i('uPremul', premul ? 1 : 0)
      .i('uR', R)
      .i('uStep', step)
      .i('uHasRefine', refine ? 1 : 0)
      .f('uSoft', Math.min(1, b.edge.feather / 4));
    drawFullscreen(gl);
    return out;
  }

  /** Push-pull fill of the solid-subject colour into the edge band (decontaminate's foreground estimate). */
  private pushPull(src: Target, matte: Target, toSrc: M3, premul: boolean, matteTile = false): Target {
    const gl = this.gl;
    let slot = 30;
    const l0 = this.target(src.width, src.height, 'rgba16f', slot++);
    l0.bind();
    this.prog('push0', () => PUSH0)
      .tex('uImg', 0, src.tex)
      .tex('uMatte', 1, matte.tex)
      .m3('uToSrc', toGl3(toSrc))
      .i('uPremul', premul ? 1 : 0)
      .i('uMatteTile', matteTile ? 1 : 0);
    drawFullscreen(gl);
    const levels = [l0];
    while (
      Math.max(levels[levels.length - 1]!.width, levels[levels.length - 1]!.height) > 4 &&
      levels.length < 14
    ) {
      const cur = levels[levels.length - 1]!;
      const dst = this.target(Math.ceil(cur.width / 2), Math.ceil(cur.height / 2), 'rgba16f', slot++);
      dst.bind();
      this.prog('halve', () => HALVE)
        .tex('uSrc', 0, cur.tex)
        .f('uSrcSize', cur.width, cur.height)
        .i('uInPremul', 1);
      drawFullscreen(gl);
      levels.push(dst);
    }
    const pull = this.prog('pull', () => PULL);
    let top = levels[levels.length - 1]!;
    let pulled = this.target(top.width, top.height, 'rgba16f', slot++);
    pulled.bind();
    pull.tex('uFine', 0, top.tex).tex('uCoarse', 1, top.tex).i('uCoarsest', 1);
    drawFullscreen(gl);
    for (let i = levels.length - 2; i >= 0; i--) {
      top = levels[i]!;
      const out = this.target(top.width, top.height, 'rgba16f', slot++);
      out.bind();
      pull.tex('uFine', 0, top.tex).tex('uCoarse', 1, pulled.tex).i('uCoarsest', 0);
      drawFullscreen(gl);
      pulled = out;
    }
    return pulled;
  }

  /** Pass 16 on a target: alpha × refined matte, decontaminate, contact shadow. */
  private bgStage(
    src: Target,
    s: Settings,
    toSrc: M3,
    premul: boolean,
    region?: Rect,
    outPerSrc = 1,
  ): Target {
    const { matte, shadow, base } = this.bgTextures(s);
    const b = s.background;
    const tileM = region ? this.tileMatte(src, s, base, toSrc, premul, region, outPerSrc) : null;
    const m = tileM ?? matte;
    const fill =
      b.edge.decontaminate > 0 ? this.pushPull(src, m, toSrc, premul, !!tileM).tex : this.blankTex();
    const out = this.target(src.width, src.height, 'rgba16f', src === this.pool.get('rgba16f:27') ? 28 : 27);
    out.bind();
    this.prog('bgApply', () => BG_APPLY)
      .tex('uImg', 0, src.tex)
      .tex('uMatte', 1, m.tex)
      .i('uMatteTile', tileM ? 1 : 0)
      .tex('uFill', 2, fill)
      .tex('uShadow', 3, shadow?.tex ?? this.blankTex())
      .m3('uToSrc', toGl3(toSrc))
      .i('uPremul', premul ? 1 : 0)
      .f('uDecon', b.edge.decontaminate)
      .i('uShadowOn', shadow ? 1 : 0)
      .f('uShadowOpacity', b.shadow.opacity / 100)
      .f('uShadowOffset', b.shadow.offsetY / 100);
    drawFullscreen(this.gl);
    return out;
  }

  /**
   * Red 50% overlay of `masks[index]` (O, §11.4) over `rect` (canvas pixels, y down). `outToSrc` maps
   * pixels of the drawn image (rect-sized) to source-normalized coordinates.
   */
  drawMaskOverlay(masks: LocalMask[], index: number, rect: Rect, canvasH: number, outToSrc: M3) {
    const gl = this.gl;
    const w = Math.max(1, Math.round(rect.w)),
      h = Math.max(1, Math.round(rect.h));
    const ox = Math.round(rect.x),
      oy = Math.round(canvasH - (rect.y + rect.h));
    const p = this.prog('maskOverlay', () => MASK_OVERLAY);
    this.maskUniforms(p, masks);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(ox, oy, w, h);
    p.m3('uOutToSrc', toGl3(outToSrc)).f('uOrigin', ox, oy).f('uOutSize', w, h).i('uIndex', index);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    drawFullscreen(gl);
    gl.blendFunc(gl.ONE, gl.ZERO);
    gl.disable(gl.BLEND);
  }

  /** Export/100% path: colour on `region` of the full-res source into a premultiplied `rw x rh` target. */
  private colorStage(
    s: Settings,
    region: Rect,
    rw: number,
    rh: number,
    guide: Target,
    input?: { tex: WebGLTexture; rect: Rect },
  ): Target {
    const f = this.feats(s);
    const p = this.prog(`color:region:${featureKey(f)}`, () => colorShader(f, 'region'));
    const out = this.target(rw, rh, 'rgba16f', 3);
    out.bind();
    const sr = input?.rect ?? region;
    p.tex('uSrc', 0, input?.tex ?? this.full!)
      .i('uEncoded', !input && this.fullInfo!.encoded ? 1 : 0)
      .f('uSrcRect', sr.x, sr.y, sr.w, sr.h)
      .f('uOutSize', rw, rh)
      .f('uRegion', region.x, region.y, region.w, region.h);
    this.colorUniforms(p, s, f, guide);
    drawFullscreen(this.gl);
    return out;
  }

  private warp(
    region: Target,
    regionRect: Rect,
    outToSrc: Float32Array,
    out: Target,
    offset: [number, number],
    cubic: boolean,
  ) {
    const p = this.prog('warp', () => WARP);
    out.bind();
    p.tex('uSrc', 0, region.tex)
      .f('uOutSize', out.width, out.height)
      .f('uOutOffset', offset[0], offset[1])
      .m3('uOutToSrc', outToSrc)
      .f('uRegion', regionRect.x, regionRect.y, regionRect.w, regionRect.h)
      .f('uRegionSize', region.width, region.height)
      .i('uCubic', cubic ? 1 : 0);
    drawFullscreen(this.gl);
  }

  private output(
    src: Target,
    w: number,
    h: number,
    o: OutputOptions,
    toCanvas: boolean,
    dst?: Target,
    origin: [number, number] = [0, 0],
  ) {
    const gl = this.gl;
    const p = this.prog('output', () => OUTPUT);
    if (toCanvas) {
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(origin[0], origin[1], w, h);
    } else dst!.bind();
    p.f('uOrigin', origin[0], origin[1]);
    p.tex('uSrc', 0, src.tex)
      .f('uOutSize', w, h)
      .m3('uToOut', toGl(workingTo(o.space)))
      .i('uAdobe', o.space === 'adobergb' ? 1 : 0)
      .i('uFlatten', o.flatten ? 1 : 0)
      .f('uFill', o.fill[0], o.fill[1], o.fill[2])
      .i('uClipping', o.clipping ? 1 : 0)
      .i('uFlipY', toCanvas ? 1 : 0)
      .i('uView', toCanvas ? (o.view ?? 0) : 0);
    this.grainUniforms(p, o, [1, 1, 0, 0]);
    drawFullscreen(gl);
  }

  /**
   * Fit-view preview: the whole crop at `outW × outH` from the preview texture.
   * `geometryOverride` lets the crop tool show the uncropped frame.
   */
  renderPreview(
    s: Settings,
    outW: number,
    outH: number,
    geometryOverride?: GeometryInput,
    opts: { superset?: boolean } = {},
  ): Target {
    this.stats.renders++;
    const guide = this.guide(s);
    // superset: one shader variant with every per-pixel colour feature on (zero settings are identities), so
    // many different looks (the 33 Simple-mode thumbnails) share one compiled program.
    const f = opts.superset ? { ...this.feats(s), ...SUPERSET } : this.feats(s);
    const g = geometryOverride ?? s.geometry;
    const m = outputToSource(g, this.fullInfo!.width, this.fullInfo!.height, outW, outH);
    const out = this.target(outW, outH, 'rgba16f', 4);
    out.bind();
    const pvTex = this.previewTex(s);
    const p = this.prog(`color:warpLinear:${featureKey(f)}`, () => colorShader(f, 'warpLinear'));
    p.tex('uSrc', 0, pvTex)
      .f('uOutSize', outW, outH)
      .f('uOrigin', 0, 0)
      .f('uCanvasH', 0)
      .m3('uOutToSrc', toGl3(m));
    this.colorUniforms(p, s, f, guide);
    drawFullscreen(this.gl);
    if (!needsPasses(s)) return out;
    // Spatial radii shrink with the preview scale (output px per source px).
    const [x0] = apply3(m, 0, 0);
    const [x1] = apply3(m, 1, 0);
    const scale = Math.min(1, 1 / Math.max(1e-6, Math.abs(x1 - x0) * this.fullInfo!.width));
    let cur = out;
    if (hasSkin(s)) cur = this.skinStage(cur, s, m, scale);
    if (hasDetail(s)) cur = this.detailStage(cur, s.detail, false, scale);
    if (hasBackground(s) && this.matteTex) cur = this.bgStage(cur, s, m, false);
    if (hasVignette(s)) cur = this.vignette(cur, s, [0, 0], [outW, outH]);
    return cur;
  }

  /**
   * Detail (§6.6) on a target of the same size: noise reduction then sharpening, see detailPasses.ts.
   * `premul` says whether `src` is premultiplied; the result uses the same convention.
   */
  private detailStage(src: Target, d: Settings['detail'], premul: boolean, scale: number): Target {
    const gl = this.gl;
    const w = src.width,
      h = src.height;
    let cur = src;
    if (d.nrLuma > 0 || d.nrColor > 0) {
      const lab = this.target(w, h, 'rgba16f', 13);
      lab.bind();
      this.prog('d:toLab', () => TO_LAB)
        .tex('uSrc', 0, cur.tex)
        .i('uPremul', premul ? 1 : 0);
      drawFullscreen(gl);
      const nr = this.target(w, h, 'rgba16f', 14);
      nr.bind();
      this.prog('d:nr', () => NR)
        .tex('uLab', 0, lab.tex)
        .i('uPremul', premul ? 1 : 0)
        .f('uLuma', d.nrLuma)
        .f('uColor', d.nrColor)
        .f('uSigmaR', nrRangeSigma(d.nrLuma))
        .f('uScale', scale);
      drawFullscreen(gl);
      cur = nr;
    }
    if (d.sharpAmount > 0) {
      const luma = this.target(w, h, 'r16f', 15);
      luma.bind();
      this.prog('d:luma', () => SHARP_LUMA)
        .tex('uSrc', 0, cur.tex)
        .i('uPremul', premul ? 1 : 0);
      drawFullscreen(gl);
      const sigma = Math.max(0.3, d.sharpRadius * scale);
      const r = Math.min(Math.ceil(sigma * 3), 12);
      const tmp = this.target(w, h, 'r16f', 16);
      const blur = this.target(w, h, 'r16f', 17);
      const bp = this.prog('d:blur', () => DETAIL_BLUR);
      tmp.bind();
      bp.tex('uSrc', 0, luma.tex).f('uDir', 1, 0).f('uSigma', sigma).i('uR', r);
      drawFullscreen(gl);
      blur.bind();
      bp.tex('uSrc', 0, tmp.tex).f('uDir', 0, 1);
      drawFullscreen(gl);
      const [t0, t1] = maskThresholds(d.sharpMasking);
      const out = this.target(w, h, 'rgba16f', cur === src ? 14 : 18);
      out.bind();
      this.prog('d:sharp', () => SHARP_COMBINE)
        .tex('uSrc', 0, cur.tex)
        .i('uPremul', premul ? 1 : 0)
        .tex('uLuma', 1, luma.tex)
        .tex('uBlur', 2, blur.tex)
        .f('uAmount', d.sharpAmount)
        .f('uMasking', d.sharpMasking)
        .f('uThresh', t0, t1);
      drawFullscreen(gl);
      cur = out;
    }
    return cur;
  }

  /**
   * Fit-view preview straight to the canvas in ONE full-resolution pass (geometry + colour + output).
   * `rect` is in canvas pixels (y down); `scissorX` limits drawing to x < scissorX (split view).
   */
  drawPreview(
    s: Settings,
    rect: Rect,
    canvasH: number,
    o: OutputOptions = PREVIEW_OUTPUT,
    geometryOverride?: GeometryInput,
    scissorX?: number,
  ) {
    this.stats.renders++;
    const gl = this.gl;
    const guide = this.guide(s);
    const f = this.feats(s);
    const w = Math.max(1, Math.round(rect.w));
    const h = Math.max(1, Math.round(rect.h));
    const g = geometryOverride ?? s.geometry;
    const m = outputToSource(g, this.fullInfo!.width, this.fullInfo!.height, w, h);
    const ox = Math.round(rect.x);
    const oy = Math.round(canvasH - (rect.y + rect.h));
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(ox, oy, w, h);
    if (scissorX !== undefined) {
      gl.enable(gl.SCISSOR_TEST);
      gl.scissor(0, 0, Math.max(0, Math.round(scissorX)), canvasH);
    }
    const pvTex = this.previewTex(s);
    const p = this.prog(`color:warpOutput:${featureKey(f)}`, () => colorShader(f, 'warpOutput'));
    p.tex('uSrc', 0, pvTex)
      .f('uOutSize', w, h)
      .f('uOrigin', ox, oy)
      .f('uCanvasH', canvasH)
      .m3('uOutToSrc', toGl3(m))
      .m3('uToOut', toGl(workingTo(o.space)))
      .i('uAdobe', o.space === 'adobergb' ? 1 : 0)
      .i('uFlatten', o.flatten ? 1 : 0)
      .f('uFill', o.fill[0], o.fill[1], o.fill[2])
      .i('uClipping', o.clipping ? 1 : 0);
    this.colorUniforms(p, s, f, guide);
    drawFullscreen(gl);
    if (scissorX !== undefined) gl.disable(gl.SCISSOR_TEST);
  }

  /** Presents a rendered (warped) target to the default framebuffer. */
  present(t: Target, o: OutputOptions = PREVIEW_OUTPUT) {
    this.output(t, t.width, t.height, o, true);
  }

  /**
   * Presents `t` scaled into `rect` (canvas pixels, y down) of a canvas `canvasH` tall. `scissorX`
   * limits drawing to x < scissorX (split before/after view).
   */
  presentAt(t: Target, rect: Rect, canvasH: number, o: OutputOptions = PREVIEW_OUTPUT, scissorX?: number) {
    const gl = this.gl;
    const y = canvasH - (rect.y + rect.h); // GL viewport origin is bottom-left
    if (scissorX !== undefined) {
      gl.enable(gl.SCISSOR_TEST);
      gl.scissor(0, 0, Math.max(0, Math.round(scissorX)), canvasH);
    }
    this.output(t, Math.round(rect.w), Math.round(rect.h), o, true, undefined, [
      Math.round(rect.x),
      Math.round(y),
    ]);
    if (scissorX !== undefined) gl.disable(gl.SCISSOR_TEST);
  }

  /**
   * Premultiplied linear block of the preview texture covering source-normalized [u0,u1]×[v0,v1] (clamped).
   * `sx, sy` = preview pixels per source pixel; `x0, y0` = the block origin in preview pixels.
   */
  readPreviewRect(u0: number, v0: number, u1: number, v1: number) {
    const pv = this.preview;
    const info = this.fullInfo;
    if (!pv || !info) return null;
    const x0 = Math.max(0, Math.floor(u0 * pv.width)),
      y0 = Math.max(0, Math.floor(v0 * pv.height));
    const x1 = Math.min(pv.width, Math.ceil(u1 * pv.width)),
      y1 = Math.min(pv.height, Math.ceil(v1 * pv.height));
    if (x1 <= x0 || y1 <= y0) return null;
    return {
      data: readFloat(this.gl, pv, x0, y0, x1 - x0, y1 - y0),
      w: x1 - x0,
      h: y1 - y0,
      x0,
      y0,
      sx: pv.width / info.width,
      sy: pv.height / info.height,
    };
  }

  /** Average linear (pre-WB, pre-exposure) colour of a (2r+1)² block of the preview texture (eyedropper). */
  readPreviewBlock(u: number, v: number, r = 2): [number, number, number] | null {
    const pv = this.preview;
    if (!pv || u < 0 || v < 0 || u > 1 || v > 1) return null;
    const cx = Math.min(pv.width - 1, Math.max(0, Math.floor(u * pv.width)));
    const cy = Math.min(pv.height - 1, Math.max(0, Math.floor(v * pv.height)));
    const x0 = Math.max(0, cx - r),
      y0 = Math.max(0, cy - r);
    const x1 = Math.min(pv.width, cx + r + 1),
      y1 = Math.min(pv.height, cy + r + 1);
    const d = readFloat(this.gl, pv, x0, y0, x1 - x0, y1 - y0);
    // The preview is premultiplied: sum premultiplied colour and alpha, then divide.
    let R = 0,
      G = 0,
      B = 0,
      A = 0;
    for (let i = 0; i < d.length; i += 4) {
      R += d[i]!;
      G += d[i + 1]!;
      B += d[i + 2]!;
      A += d[i + 3]!;
    }
    return A > 0 ? [R / A, G / A, B / A] : null;
  }

  /**
   * Full-resolution tile (§7): the output-pixel rect `tile` of an output of `full` size. The colour stage
   * runs on the inverse-mapped source region plus an apron; large-scale context comes from the guide.
   * Used by the 100% viewer and by export. Returns the warped (straight-alpha linear) target.
   */
  renderTile(
    s: Settings,
    full: { width: number; height: number },
    tile: Rect,
    cubic = true,
    /** Final output pixels per pixel of `full` (an export that is resized afterwards: < 1). */
    outScale = 1,
  ): Target {
    this.stats.renders++;
    const info = this.fullInfo!;
    const guide = this.guide(s);
    const m = outputToSource(s.geometry, info.width, info.height, full.width, full.height);
    const apron = hasSkin(s)
      ? Math.max(
          APRON,
          Math.ceil(3 * skinRadius(info.width, info.height) + 3 * skinBlotchRadius(info.width, info.height)) +
            4,
        )
      : APRON;
    let region = sourceRegionForTile(m, tile, info.width, info.height, apron);
    // Heal sources outside the tile: grow the region to include them, unless that would more than double
    // it (a far clone source), in which case the tile reads a healed full-resolution copy (S10).
    let healed: Target | null = null;
    if (region && s.heal.length) {
      const grown = healRegion(region, s.heal, info.width, info.height);
      if (grown.w * grown.h > 2 * region.w * region.h) healed = this.healedFull(s);
      else region = grown;
    }
    if (!region) {
      const empty = this.target(tile.w, tile.h, 'rgba16f', 4);
      empty.bind();
      this.gl.clearColor(0, 0, 0, 0);
      this.gl.clear(this.gl.COLOR_BUFFER_BIT);
      return empty;
    }
    const rw = Math.round(region.w * info.width);
    const rh = Math.round(region.h * info.height);
    let input: { tex: WebGLTexture; rect: Rect } | undefined;
    if (healed) input = { tex: healed.tex, rect: region };
    else if (s.heal.length) {
      // Pass 1: the source region (linear, straight alpha) with the spots composited over it.
      const a = this.target(rw, rh, 'rgba16f', 20);
      this.downsample(this.full!, info, region, a, false, false);
      const map: [number, number, number, number] = [
        region.x * info.width,
        region.y * info.height,
        (region.w * info.width) / rw,
        (region.h * info.height) / rh,
      ];
      this.applyHeal(a, map, s.heal);
      input = { tex: a.tex, rect: { x: 0, y: 0, w: 1, h: 1 } };
    }
    let colored = this.colorStage(s, region, rw, rh, guide, input);
    if (hasSkin(s)) {
      const toSrc: M3 = [region.w / rw, 0, region.x, 0, region.h / rh, region.y, 0, 0, 1];
      colored = this.skinStage(colored, s, toSrc, 1);
    }
    if (hasDetail(s)) colored = this.detailStage(colored, s.detail, true, 1);
    if (hasBackground(s) && this.matteTex) {
      const toSrc: M3 = [region.w / rw, 0, region.x, 0, region.h / rh, region.y, 0, 0, 1];
      // Output pixels per source pixel (m maps output px → normalized source; perspective is ignored here).
      const srcPerOut = Math.sqrt(Math.abs(m[0] * m[4] - m[1] * m[3]) * info.width * info.height);
      const outPerSrc = outScale / Math.max(1e-6, srcPerOut);
      colored = this.bgStage(colored, s, toSrc, true, region, outPerSrc);
    }
    const warped = this.target(tile.w, tile.h, 'rgba16f', 4);
    this.warp(colored, region, toGl3(m), warped, [tile.x, tile.y], cubic);
    return hasVignette(s) ? this.vignette(warped, s, [tile.x, tile.y], [full.width, full.height]) : warped;
  }

  /**
   * One export tile (§7, §6.2 passes 17–19): renders the needed full-resolution region (colour on the
   * source region + apron → premultiplied Catmull-Rom warp), resizes it (halving pyramid + separable
   * Lanczos-3, premultiplied linear), then applies the output transform + output sharpening and reads
   * back the INNER rect as encoded floats (rows top-down).
   */
  exportTile(
    s: Settings,
    full: { width: number; height: number },
    t: TilePlan,
    o: OutputOptions,
    sharpen: { radius: number; amount: number },
    readAs: 'float' | 'half' = 'float',
  ): Float32Array | Uint16Array {
    const gl = this.gl;
    let src: Target;
    if (!t.full || !t.lanczos) {
      src = this.renderTile(s, full, t.outer, true);
    } else {
      // The resize after this (pyramid + Lanczos) shrinks by 2^levels · sx; the matte step needs to know.
      const outScale = 1 / (2 ** t.levels * Math.min(t.lanczos.sx, t.lanczos.sy));
      const rendered = this.renderTile(s, full, t.full, true, outScale);
      // Pyramid: 2×2 box levels (premultiplied). Level 0 needs at least a premultiply.
      let cur = rendered;
      let premul = false;
      for (let l = 0; l < t.levels; l++) {
        const w = Math.ceil(cur.width / 2),
          h = Math.ceil(cur.height / 2);
        const dst = this.target(w, h, 'rgba16f', 7 + (l % 2));
        dst.bind();
        this.prog('halve', () => HALVE)
          .tex('uSrc', 0, cur.tex)
          .f('uSrcSize', cur.width, cur.height)
          .i('uInPremul', premul ? 1 : 0);
        drawFullscreen(gl);
        cur = dst;
        premul = true;
      }
      if (!premul) {
        const dst = this.target(cur.width, cur.height, 'rgba16f', 7);
        dst.bind();
        this.prog('premul', () => PREMUL)
          .tex('uSrc', 0, cur.tex)
          .f('uSrcSize', cur.width, cur.height);
        drawFullscreen(gl);
        cur = dst;
      }
      const lz = this.prog('lanczos', () => LANCZOS);
      const hpass = this.target(t.outer.w, cur.height, 'rgba16f', 9);
      hpass.bind();
      lz.tex('uSrc', 0, cur.tex)
        .f('uSrcSize', cur.width, cur.height)
        .f('uDir', 1, 0)
        .f('uScale', t.lanczos.sx)
        .f('uOffset', t.lanczos.ox)
        .i('uFinal', 0);
      drawFullscreen(gl);
      const vpass = this.target(t.outer.w, t.outer.h, 'rgba16f', 10);
      vpass.bind();
      lz.tex('uSrc', 0, hpass.tex)
        .f('uSrcSize', hpass.width, hpass.height)
        .f('uDir', 0, 1)
        .f('uScale', t.lanczos.sy)
        .f('uOffset', t.lanczos.oy)
        .i('uFinal', 1);
      drawFullscreen(gl);
      src = vpass;
    }
    const dst = this.target(t.inner.w, t.inner.h, 'rgba16f', 11);
    dst.bind();
    this.prog('outputSharpen', () => OUTPUT_SHARPEN)
      .tex('uSrc', 0, src.tex)
      .f('uSrcSize', src.width, src.height)
      .f('uInnerOrigin', t.inner.x - t.outer.x, t.inner.y - t.outer.y)
      .m3('uToOut', toGl(workingTo(o.space)))
      .i('uAdobe', o.space === 'adobergb' ? 1 : 0)
      .i('uFlatten', o.flatten ? 1 : 0)
      .f('uFill', o.fill[0], o.fill[1], o.fill[2])
      .f('uAmount', sharpen.amount)
      .f('uRadius', sharpen.radius);
    this.grainUniforms(
      this.prog('outputSharpen', () => OUTPUT_SHARPEN),
      o,
      [1, 1, t.inner.x, t.inner.y],
    );
    drawFullscreen(gl);
    return readAs === 'half' && this.halfReadback ? readHalf(gl, dst) : readFloat(gl, dst);
  }

  /**
   * Whether RGBA16F targets can be read back as HALF_FLOAT (4× less data than FLOAT; measured 22.9 vs
   * 83.5 ms per 2048² tile on Iris Xe). Used for 8-bit exports, whose precision it exceeds.
   */
  get halfReadback(): boolean {
    if (this.halfOk === null) {
      const probe = this.target(1, 1, 'rgba16f', 12);
      probe.bind();
      this.halfOk = this.gl.getParameter(this.gl.IMPLEMENTATION_COLOR_READ_TYPE) === this.gl.HALF_FLOAT;
    }
    return this.halfOk;
  }

  /** Reads a linear (straight alpha) target, e.g. a renderPreview result, as Float32 RGBA. */
  readLinear(t: Target): Float32Array {
    return readFloat(this.gl, t);
  }

  /** Applies the output transform into a float target and reads it back (row 0 = top). */
  readOutput(t: Target, o: OutputOptions): Float32Array {
    const dst = this.target(t.width, t.height, 'rgba16f', 5);
    this.output(t, t.width, t.height, o, false, dst);
    return readFloat(this.gl, dst);
  }

  /** Small RGBA8 readback for the histogram (≤ maxEdge). */
  readSmall(
    t: Target,
    maxEdge = 512,
    o: OutputOptions = PREVIEW_OUTPUT,
  ): { data: Uint8Array; width: number; height: number } {
    const s = Math.min(1, maxEdge / Math.max(t.width, t.height));
    const w = Math.max(1, Math.round(t.width * s));
    const h = Math.max(1, Math.round(t.height * s));
    const dst = this.target(w, h, 'rgba16f', 6);
    this.output(t, w, h, o, false, dst);
    const f = readFloat(this.gl, dst);
    const data = new Uint8Array(f.length);
    for (let i = 0; i < f.length; i++) data[i] = Math.round(Math.min(1, Math.max(0, f[i]!)) * 255);
    return { data, width: w, height: h };
  }
}

/**
 * Source region needed to render `tile` (output pixels): the inverse-mapped tile, grown by `apron`
 * source pixels and snapped to the source pixel grid, clamped to the image. Null when the tile maps
 * entirely outside the source. Pure — unit tested.
 */
/**
 * Grows a tile's source region so every heal/clone spot touching it also has its source patch and the
 * low-pass neighbourhood (2.5 r) inside the region.
 */
export function healRegion(region: Rect, spots: HealSpot[], W: number, H: number): Rect {
  let x0 = region.x * W,
    y0 = region.y * H,
    x1 = (region.x + region.w) * W,
    y1 = (region.y + region.h) * H;
  // Repeat until nothing grows: a spot whose source is another spot's destination pulls that spot in,
  // and then that spot's own source too (CODE_REVIEW S9).
  for (let changed = true, pass = 0; changed && pass <= spots.length; pass++) {
    changed = false;
    const rx0 = x0,
      ry0 = y0,
      rx1 = x1,
      ry1 = y1;
    for (const sp of spots) {
      if (!sp.stroke.points.length) continue;
      const r = (sp.stroke.size / 2) * Math.min(W, H) * 3.5;
      const xs = sp.stroke.points.map((p) => p.x * W),
        ys = sp.stroke.points.map((p) => p.y * H);
      const bx0 = Math.min(...xs) - r,
        by0 = Math.min(...ys) - r,
        bx1 = Math.max(...xs) + r,
        by1 = Math.max(...ys) + r;
      if (bx1 < rx0 || bx0 > rx1 || by1 < ry0 || by0 > ry1) continue;
      const ox = sp.offset.x * W,
        oy = sp.offset.y * H;
      const nx0 = Math.min(x0, bx0, bx0 + ox),
        ny0 = Math.min(y0, by0, by0 + oy),
        nx1 = Math.max(x1, bx1, bx1 + ox),
        ny1 = Math.max(y1, by1, by1 + oy);
      if (nx0 < x0 || ny0 < y0 || nx1 > x1 || ny1 > y1) changed = true;
      x0 = nx0;
      y0 = ny0;
      x1 = nx1;
      y1 = ny1;
    }
  }
  x0 = Math.max(0, Math.floor(x0));
  y0 = Math.max(0, Math.floor(y0));
  x1 = Math.min(W, Math.ceil(x1));
  y1 = Math.min(H, Math.ceil(y1));
  return { x: x0 / W, y: y0 / H, w: (x1 - x0) / W, h: (y1 - y0) / H };
}

export function sourceRegionForTile(
  outToSrc: import('./geometry').M3,
  tile: Rect,
  W: number,
  H: number,
  apron: number,
): Rect | null {
  const pts: [number, number][] = [];
  const steps = 4; // sample the tile border: perspective edges are straight, but cheap insurance
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    for (const [x, y] of [
      [tile.x + t * tile.w, tile.y],
      [tile.x + t * tile.w, tile.y + tile.h],
      [tile.x, tile.y + t * tile.h],
      [tile.x + tile.w, tile.y + t * tile.h],
    ] as const)
      pts.push(apply3(outToSrc, x, y));
  }
  let x0 = Infinity,
    y0 = Infinity,
    x1 = -Infinity,
    y1 = -Infinity;
  for (const [u, v] of pts) {
    x0 = Math.min(x0, u * W);
    y0 = Math.min(y0, v * H);
    x1 = Math.max(x1, u * W);
    y1 = Math.max(y1, v * H);
  }
  x0 = Math.max(0, Math.floor(x0 - apron));
  y0 = Math.max(0, Math.floor(y0 - apron));
  x1 = Math.min(W, Math.ceil(x1 + apron));
  y1 = Math.min(H, Math.ceil(y1 + apron));
  if (x1 <= x0 || y1 <= y0) return null;
  return { x: x0 / W, y: y0 / H, w: (x1 - x0) / W, h: (y1 - y0) / H };
}

/** §6.5 colour stage flags. */
export function colorFlags(s: Settings): { color: boolean; hsl: boolean } {
  const hsl = HUE_BANDS.some((b) => s.hsl[b].h !== 0 || s.hsl[b].s !== 0 || s.hsl[b].l !== 0);
  return { hsl, color: hsl || s.color.vibrance !== 0 || s.color.saturation !== 0 };
}
